<i18n
  src="@/locales/views/packing_house/quality_control/process/edit_inspection.yml"
  lang="yaml"
></i18n>
<i18n src="@/locales/models/quality_inspect.yml" lang="yaml"></i18n>
<template>
  <div>
    <go-back-link
      :text="$t('labels.go_back_link')"
      name="ph_quality_control_process_show_inspect_path"
      :params="{
        harvested_batch_id: harvested_batch?.id,
        quality_inspect_id: quality_inspect_id,
        packing_house_process_id: packing_house_process_id,
      }"
    />

    <bread-crumbs
      :labels="[
        module_name,
        packing_house_process?.name,
        harvested_batch?.batch_code,
        template?.name,
      ]"
    >
    </bread-crumbs>
    <template v-if="harvested_batch && quality_inspect">
      <harvested-batch-info
        v-if="harvested_batch"
        :harvested_batch="harvested_batch"
      />
      <div class="mt-8">
        <div class="panel">
          <div class="panel-header">
            <h3 class="panel-header-title">
              {{ $t("labels.title") }}
            </h3>
          </div>

          <div class="panel-body">
            <div
              class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6 w-full pb-6"
            >
              <div class="sm:col-span-3">
                <label
                  for="first-name"
                  class="block text-sm font-medium text-gray-700"
                >
                  {{ $t("model.quality_inspect.started_at") }}
                </label>
                <div
                  class="mt-1 relative rounded-md shadow-sm"
                  v-if="dates_charged"
                >
                  <date-form
                    v-model="started_at"
                    mode="dateTime"
                    :first-day-of-week="2"
                    locale="es"
                    show-iso-weeknumbers
                  ></date-form>
                </div>
              </div>
              <div class="sm:col-span-3">
                <template v-if="dates_charged && render_finished_at">
                  <label
                    for="first-name"
                    class="block text-sm font-medium text-gray-700"
                  >
                    {{ $t("model.quality_inspect.finished_at") }}
                  </label>
                  <div class="mt-1 relative rounded-md shadow-sm">
                    <date-form
                      v-model="finished_at"
                      mode="dateTime"
                      :first-day-of-week="2"
                      locale="es"
                      show-iso-weeknumbers
                    ></date-form>
                  </div>
                </template>
              </div>
              <div class="sm:col-span-3" v-if="template?.control_exportation">
                <label
                  for="origin_export_id"
                  class="block text-sm font-medium text-gray-700"
                >
                  Productor(a)
                </label>
                <div class="mt-1">
                  <select-form
                    v-if="
                      exported_batch_id && exported_batch_options.length > 0
                    "
                    v-model="exported_batch_id"
                    :options="exported_batch_options"
                    :disabled="true"
                  />
                </div>
              </div>
              <div
                class="sm:col-span-3"
                v-if="
                  template &&
                  template.use_samplings &&
                  !template?.specific_sampling
                "
              >
                <label
                  for="first-name"
                  class="block text-sm font-medium text-gray-700"
                >
                  {{ $t("model.quality_inspect.samplings") }}
                </label>
                <div class="mt-1">
                  <input
                    disabled
                    type="text"
                    v-model.lazy.number="samplings"
                    class="input-text"
                  />
                </div>
              </div>
            </div>
            <template v-if="template && quality_inspect_data_charged">
              <sampling-layout
                v-if="template.use_samplings"
                v-model="quality_samplings"
                :samplings="samplings"
                :quality_template="template"
                :default_quality_samplings="default_quality_samplings"
              />
              <general-layout
                v-else
                v-model="quality_generals"
                :quality_template="template"
                :default_quality_generals="default_quality_generals"
              />
            </template>
          </div>
          <div class="panel-footer">
            <div class="panel-footer-actions">
              <white-button
                @click.prevent="gotToShowQualityInspect()"
                :label="$t('labels.cancel_button')"
              />
              <span class="space-actions"></span>
              <success-button
                v-if="!charging_submit"
                @click.prevent="submit()"
                :label="$t('labels.save_button')"
              />
              <success-button-processing
                v-else
                :label="$t('labels.save_button')"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
    <loading-message v-else />
  </div>
</template>

<script>
import Requester from "@/views/packing_house/requesters/quality_control/process/edit_inspect.js";

import HarvestedBatchInfo from "@/components/views/packing_house/quality_control/process/HarvestedBatchInfo.vue";
import GoBackLink from "@/components/views/layout/GoBackLink";
import SamplingLayout from "@/components/views/layout/quality_control/SamplingLayout.vue";
import GeneralLayout from "@/components/views/layout/quality_control/GeneralLayout.vue";

import moment from "moment";
import BreadCrumbs from "@/components/views/layout/BreadCrumbs.vue";

export default {
  components: {
    HarvestedBatchInfo,
    GoBackLink,
    BreadCrumbs,
    SamplingLayout,
    GeneralLayout,
  },
  props: {
    packing_house_id: {
      required: true,
    },
    packing_house_process_id: {
      required: true,
    },
    harvested_batch_id: {
      required: true,
    },
    packing_process_quality_template_id: {
      required: true,
    },
    quality_inspect_id: {
      required: true,
    },
  },
  data() {
    return {
      packing_house_process: {},
      charging_submit: false,
      harvested_batch: null,
      template: null,
      samplings: null,
      quality_inspect: null,
      started_at: null,
      finished_at: null,
      dates_charged: false,
      quality_inspect_data_charged: false,
      exported_batch: null,
      exported_batch_id: null,
      default_quality_samplings: [],
      default_quality_generals: [],
      exported_batches: [],
      exported_batch_options: [],
      quality_generals: [],
      quality_samplings: [],
    };
  },
  computed: {
    can_send: function () {
      return this.valid_data && !this.charging_submit;
    },
    started_at_formatted: function () {
      let started_at = this.started_at;
      if (started_at) return moment(started_at).format("YYYY-MM-DD hh:mm a");
      return "";
    },
    finished_at_formatted: function () {
      let finished_at = this.finished_at;
      if (finished_at) return moment(finished_at).format("YYYY-MM-DD hh:mm a");
      return "";
    },
    save_link_class: function () {
      let basic_class = "btn-main";
      if (this.can_send) return basic_class;
      return `${basic_class}  opacity-50 cursor-not-allowed`;
    },
    valid_data: function () {
      return (
        Boolean(this.started_at) &&
        (Boolean(this.samplings) || !this.template?.use_samplings)
      );
    },
  },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          packing_house_id: this.packing_house_id,
        },
      }).then(() => {
        this.checkIfAuthenticated();
        this.fetchPackingHouseProcess();
        this.fetchHarvestedBatch();
        this.fetchTemplate().then(() => {
          //   if (this.template?.control_exportation) this.fetchExportedBatches();
        });
        this.fetchQualityInspect();
      })
    );
  },
  methods: {
    fetchPackingHouseProcess: async function () {
      let response = await Requester.fetchPackingHouseProcess({
        packing_house_id: this.packing_house_id,
        packing_house_process_id: this.packing_house_process_id,
      });
      this.packing_house_process = response.data;
    },
    exportedBatchOption: function (exported_batch) {
      let label = "";
      if (exported_batch.producer) {
        label += exported_batch.producer;
        if (exported_batch.organization)
          label += ` (${exported_batch.organization})`;
      } else label += exported_batch.organization;
      return {
        name: label,
        id: exported_batch.id,
      };
    },
    fetchHarvestedBatch: async function () {
      let response = await Requester.fetchHarvestedBatch({
        packing_house_id: this.packing_house_id,
        packing_house_process_id: this.packing_house_process_id,
        harvested_batch_id: this.harvested_batch_id,
      });
      this.harvested_batch = response.data;
    },
    fetchExportedBatches: async function () {
      let response = await Requester.fetchExportedBatches({
        harvested_batch_id: this.harvested_batch_id,
      });
      this.exported_batches = response.data;
      this.exported_batches.forEach((exported_batch) => {
        this.exported_batch_options.push(
          this.exportedBatchOption(exported_batch)
        );
      });
    },
    fetchTemplate: async function () {
      let response = await Requester.fetchTemplate({
        packing_process_quality_template_id:
          this.packing_process_quality_template_id,
        harvested_batch_id: this.harvested_batch_id,
        packing_house_id: this.packing_house_id,
        packing_house_process_id: this.packing_house_process_id,
      });
      this.template = response.data;
      if (this.template?.specific_sampling)
        this.samplings = this.template?.samplings;
    },
    fetchQualityInspect: async function () {
      let response = await Requester.fetchExportQualityInspect({
        packing_house_id: this.packing_house_id,
        packing_house_process_id: this.packing_house_process_id,
        packing_process_quality_template_id:
          this.packing_process_quality_template_id,
        quality_inspect_id: this.quality_inspect_id,
      });
      this.quality_inspect = response.data;
      if (this.quality_inspect.started_at)
        this.started_at = this.quality_inspect.started_at.split(".")[0];
      if (this.quality_inspect.finished_at)
        this.finished_at = this.quality_inspect.finished_at.split(".")[0];
      this.exported_batch_id = this.quality_inspect.exported_batch_id;
      this.samplings = this.quality_inspect.samplings;
      this.default_quality_samplings = this.quality_inspect.quality_samplings;
      this.default_quality_generals = this.quality_inspect.quality_generals;
      this.dates_charged = true;
      this.quality_inspect_data_charged = true;
    },
    gotToShowQualityInspect: function () {
      this.$router.push({
        name: "ph_quality_control_process_show_inspect_path",
        params: {
          harvested_batch_id: this.harvested_batch_id,
          quality_template_id: this.quality_template?.id,
          packing_house_process_id: this.packing_house_process_id,
          packing_house_id: this.packing_house_id,
          packing_process_quality_template_id:
            this.packing_process_quality_template_id,
          quality_inspect_id: this.quality_inspect_id,
        },
      });
    },
    submit: async function () {
      if (!this.can_send) return;
      this.charging_submit = true;
      if (this.template?.use_samplings) await this.submitWithSamplings();
      else this.submitWithoutSamplings();
      this.charging_submit = false;
    },
    submitWithSamplings: async function () {
      try {
        let response = await Requester.updateExportQualityInspect({
          quality_inspect_id: this.quality_inspect_id,
          packing_house_id: this.packing_house_id,
          packing_house_process_id: this.packing_house_process_id,
          harvested_batch_id: this.harvested_batch_id,
          packing_process_quality_template_id:
            this.packing_process_quality_template_id,
          started_at: this.started_at_formatted,
          finished_at: this.finished_at_formatted,
          exported_batch_id: this.exported_batch_id,
          samplings: this.samplings,
          quality_samplings: this.quality_samplings,
        });
        this.registerSuccessNotification({
          title: response.data.success_message,
        });
        this.gotToShowQualityInspect();
      } catch (error) {
        console.log(error);
      }
    },
    submitWithoutSamplings: async function () {
      try {
        let response = await Requester.updateWithoutSamplings({
          quality_inspect_id: this.quality_inspect_id,
          packing_house_id: this.packing_house_id,
          packing_house_process_id: this.packing_house_process_id,
          harvested_batch_id: this.harvested_batch_id,
          packing_process_quality_template_id:
            this.packing_process_quality_template_id,
          started_at: this.started_at_formatted,
          finished_at: this.finished_at_formatted,
          exported_batch_id: this.exported_batch_id,
          quality_generals: this.quality_generals,
        });
        this.registerSuccessNotification({
          title: response.data.success_message,
        });
        this.gotToShowQualityInspect();
      } catch (error) {
        console.log(error);
      }
    },
  },
  watch: {
    started_at: {
      handler: function (started_at) {
        if (this.dates_charged && !this.finished_at) {
          this.render_finished_at = false;
          if (!started_at) return;
          started_at = moment(started_at);
          this.finished_at = started_at
            .add(1, "hours")
            .format("YYYY-MM-DD hh:mm");
        }
        this.render_finished_at = Boolean(started_at);
      },
    },
  },
};
</script>
